/**
 * ZFeatureFlags - Zod Validation Schemas for Feature Flags API
 *
 * Single source of truth for Feature Flags API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * User endpoint - protected by Clerk auth (uses organization from JWT)
 * Base path: /api/feature-flags
 */

import { z } from "zod";

import { FeatureFlag, type FeatureFlagKey } from "../../../shared/domain/feature-flags";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Get My Feature Flags Response
 *
 * Returns a record of feature flag key -> enabled status
 * Only includes flags that exist in the system
 *
 * @example
 * ```json
 * {
 *   "flags": {
 *     "surfer_agent": false,
 *     "echo_audit_logs": true,
 *     "advanced_workflows": true
 *   }
 * }
 * ```
 */
export const GetMyFeatureFlagsResponseSchema = z.object({
  flags: z.record(z.string(), z.boolean())
});
export type GetMyFeatureFlagsResponse = z.infer<typeof GetMyFeatureFlagsResponseSchema>;

/**
 * Type-safe flag record (for internal use)
 *
 * Maps FeatureFlagKey to boolean for strict typing
 */
export type FeatureFlagsRecord = Partial<Record<FeatureFlagKey, boolean>>;

// ═══════════════════════════════════════════════════════════════
// ZFEATURE_FLAGS MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZFeatureFlags - Zod API validation schemas for Feature Flags
 *
 * Single source of truth for Feature Flags API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * User endpoint - requires Clerk JWT authentication
 */
export const ZFeatureFlags = {
  /**
   * Get feature flags for current user's organization
   *
   * GET /api/feature-flags/me
   *
   * Uses Clerk JWT to determine organization, then checks
   * each feature flag's enabled status for that org.
   */
  GetMyFlags: {
    method: "GET" as const,
    path: "/api/feature-flags/me",
    Response: ApiSuccessSchema(GetMyFeatureFlagsResponseSchema)
  }
} as const;

// Re-export FeatureFlag for convenience
export { FeatureFlag };
export type { FeatureFlagKey };
